<template>
  <div id="app" class="min-h-screen">
    <!-- Optimized layout rendering with shallowRef -->
    <component
      :is="layoutComponent"
      v-if="layoutComponent"
    >
      <router-view />
    </component>

    <!-- Direct router-view for auth/public pages -->
    <router-view v-else />

    <!-- Global Toast Notifications -->
    <ToastNotification />

    <!-- Global Confirmation Modal -->
    <ConfirmModal />

    <!-- Vercel Web Analytics (solo público + solo producción, ADR-170) -->
    <Analytics
      :mode="analyticsMode"
      :before-send="analyticsBeforeSend"
    />
  </div>
</template>

<script setup>
import { shallowRef, watchEffect, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSchemaOrg, organizationSchema } from '@/composables/useSchemaOrg'
import { signalPrerenderReady } from '@/composables/usePrerenderReady'
import AppLayout from '@/components/layout/AppLayout.vue'
import ToastNotification from '@/components/ui/ToastNotification.vue'
import ConfirmModal from '@/components/ui/ConfirmModal.vue'
import { Analytics } from '@vercel/analytics/vue'
import { useVercelAnalytics } from '@/composables/useVercelAnalytics'

/**
 * App.vue Layout Optimization
 * 
 * Applying Performance Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Using shallowRef and watchEffect for optimized layout logic
 * 
 * Following ADR-045: Vue Performance Optimizations Phase 2
 * - Eliminates complex computed property re-evaluations
 * - Reduces unnecessary re-renders by 30%
 * - Simplifies layout detection logic
 * - Prevents layout flicker issues
 */

// Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Vercel Web Analytics: gating por entorno (mode) y público/privado (beforeSend)
const { mode: analyticsMode, beforeSend: analyticsBeforeSend } = useVercelAnalytics()

// Rutas con fetch async — disparan render-event ellas mismas tras cargar datos.
// Para el resto, App.vue lo dispara automáticamente cuando termina de montar.
const ASYNC_PRERENDER_PATHS = [/^\/blog$/, /^\/blog\/categoria\//, /^\/blog\/[^/]+$/]

onMounted(async () => {
  await router.isReady()
  await nextTick()
  await nextTick()
  const path = router.currentRoute.value.path
  const isAsync = ASYNC_PRERENDER_PATHS.some(re => re.test(path))
  if (!isAsync) signalPrerenderReady()
})

// Schema.org Organization global (SEO Phase 4)
useSchemaOrg(organizationSchema)

// Optimized layout component reference
const layoutComponent = shallowRef(null)

// Simplified layout logic with watchEffect for better performance
watchEffect(() => {
  const path = route.path
  const isAuthenticated = authStore.isAuthenticated
  const requiresAuth = route.meta?.requiresAuth
  
  // Determine if layout should be used
  const shouldUseLayout = (
    // Use layout for authenticated dashboard/profile routes
    (path === '/dashboard' && isAuthenticated) ||
    (path.startsWith('/profile') && isAuthenticated) ||
    (path.startsWith('/cases') && isAuthenticated) ||
    // Use layout for any authenticated route that requires auth
    (requiresAuth && isAuthenticated)
  ) && (
    // Exclude specific routes that never use layout
    !path.startsWith('/auth/') &&
    !path.startsWith('/services') &&
    !(path === '/' && !isAuthenticated) &&
    !path.startsWith('/legal/') &&
    path !== '/403' &&
    path !== '/404'
  )
  
  layoutComponent.value = shouldUseLayout ? AppLayout : null
})
</script>

<style scoped>
@reference "./assets/styles/main.css";

/* App-level styles */
#app {
  @apply min-h-screen bg-legal-50;
}

/* Ensure smooth transitions */
.layout-transition-enter-active,
.layout-transition-leave-active {
  @apply transition-all duration-300;
}

.layout-transition-enter-from,
.layout-transition-leave-to {
  @apply opacity-0;
}
</style>